import invoicesService from "@/modules/invoices/services/invoicesService";
import { i18n } from "@/plugins/i18n";
import { CURRENCIES } from '@/constansts/currency';
import taxUtil from '@/modules/payments/utils/taxUtil';
import formattingUtil from '@/utils/formattingUtil';

const discountedUnitAmount = (item) => {
  if (item.net_total == null || !item.quantity) return item.rate;
  return formattingUtil.roundNumber(item.net_total / item.quantity);
};

const buildRefundCreditNotePayload = async ({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }) => {
  const isFullRefund = parseFloat(refundAmount) === parseFloat(invoiceOriginalAmount);

  if (isFullRefund) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    const invoiceLineItems = invoice.items.concat(
      invoice.sections?.flatMap((section) => section.items) || [],
    );

    return {
      invoice_uid: invoiceUid,
      line_items: invoiceLineItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit_amount: discountedUnitAmount(item),
        taxes: item.taxes,
      })),
      notes: i18n.t("refund_dialog.credit_note_reason.full_refund"),
      source_payment_uid: sourcePaymentUid,
    };
  }

  return {
    invoice_uid: invoiceUid,
    line_items: [{
      name: i18n.t("refund_dialog.credit_note_partial_refund_item_name"),
      quantity: 1,
      unit_amount: refundAmount,
    }],
    notes: i18n.t("refund_dialog.credit_note_reason.partial_refund"),
    source_payment_uid: sourcePaymentUid,
  };
};

const getCreditNoteInvoice = (state) => {
  const invoiceUid = state.creditNoteEditor?.invoice_uid;
  return state.invoices[invoiceUid];
};

const getAvailableCreditAmount = (state) => {
  const invoice = getCreditNoteInvoice(state);
  if (!invoice?.payment_status || !invoice?.amount) return 0;

  const total = parseFloat(invoice.amount) || 0;
  const credited = parseFloat(invoice.payment_status.amount_credited) || 0;
  return formattingUtil.roundNumber(total - credited);
};

const calcCreditEditorBreakdown = (state) => {
  const invoice = getCreditNoteInvoice(state);
  if (!state.creditNoteEditor?.line_items?.length || !invoice) {
    return { netTotal: 0, taxTotal: 0, grandTotal: 0, taxesById: {} };
  }

  return state.creditNoteEditor.line_items.reduce((acc, item) => {
    const itemPrice = item.unit_amount * item.quantity;
    const itemTaxes = item.taxes || [];
    const breakdown = taxUtil.calcPriceBreakdown(invoice.metadata.tax_mode, itemPrice, itemTaxes);

    const taxesById = { ...acc.taxesById };
    itemTaxes.forEach((tax) => {
      const taxKey = `${tax.name}-${tax.rate}`;
      if (!taxesById[taxKey]) {
        taxesById[taxKey] = { name: tax.name, rate: tax.rate, total: 0 };
      }
      taxesById[taxKey].total = formattingUtil.roundNumber(taxesById[taxKey].total + tax.tax_total);
    });

    return {
      netTotal: formattingUtil.roundNumber(acc.netTotal + breakdown.netTotal),
      taxTotal: formattingUtil.roundNumber(acc.taxTotal + breakdown.taxTotal),
      grandTotal: formattingUtil.roundNumber(acc.grandTotal + breakdown.grandTotal),
      taxesById,
    };
  }, { netTotal: 0, taxTotal: 0, grandTotal: 0, taxesById: {} });
};

const state = {
  invoices: {},
  creditNoteEditor: null,
};

const getters = {
  invoiceByUid: (state) => (uid) => state.invoices[uid],
  creditNoteEditor: (state) => state.creditNoteEditor,
  creditNoteCurrencySymbol: (state) => {
    const invoice = getCreditNoteInvoice(state);
    return CURRENCIES[invoice?.currency]?.symbol || '';
  },
  creditAvailable: (state) => {
    const invoice = getCreditNoteInvoice(state);
    if (!invoice?.currency) return '';

    const available = getAvailableCreditAmount(state);
    return formattingUtil.priceDisplay(available, invoice.currency);
  },
  creditEditorSummary: (state) => {
    const { netTotal, grandTotal, taxesById } = calcCreditEditorBreakdown(state);
    const invoice = getCreditNoteInvoice(state);
    const currency = invoice?.currency;

    const formatPrice = (amount) => formattingUtil.priceDisplay(amount || 0, currency);

    const taxes = Object.values(taxesById)
      .filter((tax) => tax.total > 0)
      .map((tax) => ({
        name: tax.name,
        rate: tax.rate,
        amount: formatPrice(tax.total),
      }));

    return {
      subtotal: formatPrice(netTotal),
      taxes,
      total: formatPrice(grandTotal),
    };
  },
  isCreditExceedingLimit: (state) => {
    const availableCredit = getAvailableCreditAmount(state);
    const { grandTotal } = calcCreditEditorBreakdown(state);
    return grandTotal > availableCredit;
  },
};

const mutations = {
  setInvoice(state, invoice) {
    state.invoices = {
      ...state.invoices,
      [invoice.uid]: invoice,
    };
  },
  updateInvoice(state, updatedFields) {
    state.invoices = {
      ...state.invoices,
      [updatedFields.uid]: {
        ...state.invoices[updatedFields.uid],
        ...updatedFields,
      },
    };
  },
  setCreditNoteEditor(state, creditNote) {
    state.creditNoteEditor = creditNote;
  },
  updateCreditNoteEditor(state, updates) {
    state.creditNoteEditor = {
      ...state.creditNoteEditor,
      ...updates,
    };
  },
  resetCreditNoteEditor(state) {
    state.creditNoteEditor = null;
  },
};

const actions = {
  async fetchInvoice({ commit }, invoiceUid) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    commit("setInvoice", invoice);
    return invoice;
  },
  initializeCreditNoteEditor({ commit, state }, invoiceUid) {
    const invoice = state.invoices[invoiceUid];
    if (!invoice) return;

    const hasPreviousCreditNotes = parseFloat(invoice.payment_status?.amount_credited) > 0;

    let lineItems = [];

    if (hasPreviousCreditNotes) {
      lineItems = [{
        id: `custom-${Date.now()}`,
        name: '',
        quantity: 1,
        unit_amount: 0,
        taxes: [],
        isFromInvoice: false,
      }];
    } else {
      const invoiceLineItems = (invoice.items || []).concat(
        invoice.sections?.flatMap((section) => section.items) || [],
      );

      lineItems = invoiceLineItems.map((item, index) => ({
        id: `${invoiceUid}-${index}`,
        name: item.name,
        quantity: item.quantity,
        unit_amount: discountedUnitAmount(item),
        taxes: item.taxes || [],
        isFromInvoice: true,
        maxQuantity: item.quantity,
      }));
    }

    const creditNote = {
      invoice_uid: invoiceUid,
      line_items: lineItems,
      notes: '',
    };

    commit('setCreditNoteEditor', creditNote);
  },
  resetCreditNoteEditor({ commit }) {
    commit('resetCreditNoteEditor');
  },
  updateCreditNoteEditor({ commit }, updates) {
    commit('updateCreditNoteEditor', updates);
  },
  updateCreditNoteLineItem({ commit, state }, { itemId, updates }) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const updatedLineItems = lineItems.map((item) => {
      if (item.id !== itemId) return item;
      return { ...item, ...updates };
    });
    commit('updateCreditNoteEditor', { line_items: updatedLineItems });
  },
  addCustomCreditNoteItem({ commit, state }) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const newItem = {
      id: `custom-${Date.now()}`,
      name: '',
      quantity: 1,
      unit_amount: 0,
      taxes: [],
      isFromInvoice: false,
    };
    commit('updateCreditNoteEditor', { line_items: [...lineItems, newItem] });
  },
  removeCreditNoteLineItem({ commit, dispatch, state }, itemId) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const updatedLineItems = lineItems.filter((item) => item.id !== itemId);
    commit('updateCreditNoteEditor', { line_items: updatedLineItems });
    if (updatedLineItems.length === 0) {
      dispatch('addCustomCreditNoteItem');
    }
  },
  clearCreditNoteItems({ commit, dispatch }) {
    commit('updateCreditNoteEditor', { line_items: [] });
    dispatch('addCustomCreditNoteItem');
  },
  async cancelInvoice({ commit }, { payableUid, payload }) {
    const cancelInvoiceResponse = await invoicesService.cancelInvoice(payableUid, payload);
    commit("updateInvoice", cancelInvoiceResponse.data.invoice);
    return cancelInvoiceResponse;
  },
  async createCreditNote(
    { dispatch },
    { creditNotePayload, invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }
  ) {
    const payload = creditNotePayload
      || await buildRefundCreditNotePayload({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount });

    const response = await invoicesService.createCreditNote(payload);

    if (sourcePaymentUid) {
      dispatch("PaymentsStore/fetchPayment", { uid: sourcePaymentUid }, { root: true });
    }

    return response;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
